import {
  CreateSurveySessionResponse,
  HelpCenterApiResponse,
  LiveChatCreateArgs,
  LiveChatCreateResponseData,
  LiveChatRefreshTokenArgs,
  LiveChatRefreshTokenResponseData,
  SurveySessionCreateArgs,
  SurveySessionUpdateArgs,
  UpdateSurveySessionResponse,
  getHelpCenterApiServiceEndpoint,
  helpCenterEndpoints,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import Client from "./client";

const headerDefaults = {
  "Content-type": "application/json",
};

class HelpCenterApi extends Client {
  constructor() {
    const serviceEndpoint = getHelpCenterApiServiceEndpoint();
    super(serviceEndpoint);
  }

  createLiveChat = async (liveChatData: LiveChatCreateArgs) => {
    const response = await this.post<LiveChatCreateResponseData>({
      path: helpCenterEndpoints.createLiveChat(),
      body: JSON.stringify(liveChatData),
    });

    return response;
  };

  refreshToken = async (body: LiveChatRefreshTokenArgs) => {
    const response = await this.post<LiveChatRefreshTokenResponseData>({
      path: helpCenterEndpoints.refreshLiveChatToken(),
      headers: headerDefaults,
      body: JSON.stringify(body),
    });

    return response as HelpCenterApiResponse<LiveChatRefreshTokenResponseData>;
  };

  /**
   * Create a new survey session for the given survey and variant ID and get the survey questions or initial question/s.
   * @param options - The survey ID and variant ID.
   * @returns The survey session ID and the survey questions.
   */
  createSurveySession = async ({
    surveyId,
    surveyVariant,
  }: SurveySessionCreateArgs): Promise<
    HelpCenterApiResponse<CreateSurveySessionResponse>
  > => {
    const csrf = await this.getCsrf();

    const response = await this.post<CreateSurveySessionResponse>({
      path: helpCenterEndpoints.createSurveySession(surveyId),
      body: JSON.stringify({
        surveyVariant,
      }),
      headers: {
        "Content-type": "application/json",
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as HelpCenterApiResponse<CreateSurveySessionResponse>;
  };

  /**
   * Update the survey session with the embedded data and responses to the survey questions.
   * @param options - The survey session ID, survey ID, embedded data and the responses to the survey questions.
   * @returns The updated survey session, and additional questions if any.
   */
  updateSurveySession = async ({
    embeddedData,
    sessionId,
    surveyId,
    responses,
  }: SurveySessionUpdateArgs): Promise<
    HelpCenterApiResponse<UpdateSurveySessionResponse>
  > => {
    const csrf = await this.getCsrf();

    const response = await this.post<UpdateSurveySessionResponse>({
      path: helpCenterEndpoints.updateSurveySession(surveyId, sessionId),
      body: JSON.stringify({
        embeddedData,
        responses,
      }),
      headers: {
        "Content-type": "application/json",
        "x-twilio-csrf": csrf,
      },
      requestOptions: {
        credentials: "include",
      },
    });

    return response as HelpCenterApiResponse<UpdateSurveySessionResponse>;
  };
}

export default HelpCenterApi;
